import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import { FESTIVALS, IMG } from "@/lib/data";
import { ArrowRight } from "lucide-react";

export default function Festivals() {
  return (
    <div data-testid="festivals-page">
      <SEOHead
        cmsPage="festivals"
        title="TOK Hip Hop Festival & TOK XR Festival · Immersive Music & Tech Festivals · Temple of Kulture"
        description="Two flagship 2026 festivals — TOK Hip Hop Festival (summer) and TOK XR Festival (autumn). Immersive stages, projection mapping, AI storytelling and global majority sound across Medway, London & Kent."
        image={IMG.festivals}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FestivalSeries",
          name: "TOK Festivals",
          organizer: { "@type": "Organization", name: "Temple of Kulture" },
          location: { "@type": "Place", name: "Medway · London · Kent" },
        }}
      />
      <PageHero
        cmsPage="festivals"
        eyebrow="Festivals"
        title={
          <>
            Two flagship <br />
            <span className="text-amber-400">festivals.</span>
          </>
        }
        subtitle="TOK Hip Hop Festival and TOK XR Festival — celebrating culture, sound and immersive technology at scale."
        image={IMG.festivals}
        testId="festivals-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid lg:grid-cols-2 gap-6">
            {FESTIVALS.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.1}>
                <article
                  data-testid={`festival-card-${i}`}
                  className="group relative overflow-hidden border border-white/5 hover:border-amber-400/40 transition-colors aspect-[4/5] sm:aspect-[5/6]"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                    style={{ backgroundImage: `url(${f.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
                  <div className="relative h-full p-8 sm:p-12 flex flex-col justify-between">
                    <SectionLabel>{f.when}</SectionLabel>
                    <div>
                      <h2 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl sm:text-5xl">
                        {f.title}
                      </h2>
                      <p className="mt-6 text-zinc-300 leading-relaxed max-w-lg">
                        {f.desc}
                      </p>
                      <Link
                        to="/whats-on"
                        data-testid={`festival-cta-${i}`}
                        className="mt-8 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                      >
                        Get on the list <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="What's next"
        steps={[
          {
            label: "16 – 25",
            title: "Perform & produce at TOK festivals",
            desc: "LEVEL UP applicants are first in line for festival production crew and stage roles.",
            cta: "Apply for LEVEL UP",
            to: "/level-up-xr",
            testid: "festivals-next-level-up",
          },
          {
            label: "Adults",
            title: "Immersive nights all year",
            desc: "Afro Anime socials, Immersive Brunch and XR film nights between festivals.",
            cta: "Experiences",
            to: "/experiences",
            testid: "festivals-next-experiences",
          },
          {
            label: "Brands & funders",
            title: "Partner with our festivals",
            desc: "Activations, talent, content & cultural ROI — we'd love to talk.",
            cta: "Partner with us",
            to: "/corporate",
            testid: "festivals-next-corporate",
          },
        ]}
      />
    </div>
  );
}
